/**
 * Synkro CLI Authentication
 *
 * OAuth-style authentication flow for CLI integration with Synkro web platform.
 * Mirrors the Node.js reference implementation.
 */

import { createServer, IncomingMessage, ServerResponse } from "node:http";
import { writeFileSync, readFileSync, existsSync, mkdirSync, unlinkSync } from "node:fs";
import { homedir, platform } from "node:os";
import { join, dirname } from "node:path";
import { execFile } from "node:child_process";
import jwt from "jsonwebtoken";

// Types
interface WorkOSJwtPayload {
  iss: string;       // "https://api.workos.com/"
  sub: string;       // user ID
  aud?: string;      // client ID
  exp: number;
  iat: number;
  email?: string;
  org_id?: string;
  role?: string;
  permissions?: string[];
  sid?: string;      // session ID
}

// Configuration — matches the desktop app pattern (packages/desktop/src-tauri/src/auth.rs).
// Dev dashboard runs on :4322; CLI listens on 8100 for the OAuth callback.
const PORT = 8100;
// Same poisoning concern as the gateway URL: a developer's local .env or
// op:// expansion can land in SYNKRO_WEB_AUTH_URL. Only honor http(s) values;
// fall through to the prod dashboard otherwise so the OAuth callback always
// has a real origin to open the browser at.
const RAW_WEB_AUTH_URL = process.env.SYNKRO_WEB_AUTH_URL;
const SYNKRO_WEB_AUTH_URL = (RAW_WEB_AUTH_URL && /^https?:\/\//.test(RAW_WEB_AUTH_URL))
  ? RAW_WEB_AUTH_URL
  : "https://app.synkro.sh";
const AUTH_FILE = process.env.SYNKRO_AUTH_FILE || join(homedir(), ".synkro", "credentials.json");
const RAW_API_URL = process.env.SYNKRO_CRUD_URL || process.env.SYNKRO_API_URL;
const SYNKRO_API_URL = (RAW_API_URL && /^https?:\/\//.test(RAW_API_URL))
  ? RAW_API_URL
  : "https://api.synkro.sh";

// Types — matches the AuthCredentials shape returned by the dashboard's
// /api/auth/cli-callback (see packages/app/src/pages/api/auth/cli-callback.ts).
export interface AuthCredentials {
  access_token: string;
  refresh_token: string;
  user_id?: string;
  email?: string;
  org_id?: string;
  state?: string;
}

export interface UserInfo {
  id: string;
  email: string;
  org_id?: string;
}

// HTML responses
const SUCCESS_HTML = `
<!DOCTYPE html>
<html>
<head>
    <title>Authentication Successful - Synkro CLI</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            margin: 0;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        .container {
            background: white;
            padding: 3rem;
            border-radius: 1rem;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            text-align: center;
            max-width: 400px;
        }
        .checkmark {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: #10b981;
            margin: 0 auto 1.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .checkmark svg {
            width: 50px;
            height: 50px;
            stroke: white;
        }
        h1 {
            color: #1f2937;
            margin: 0 0 0.5rem;
        }
        p {
            color: #6b7280;
            margin: 0;
        }
        .close-note {
            margin-top: 1.5rem;
            font-size: 0.875rem;
            color: #9ca3af;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="checkmark">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
            </svg>
        </div>
        <h1>Authentication Successful!</h1>
        <p>Your Synkro CLI has been authenticated.</p>
        <p class="close-note">You can close this window and return to your terminal.</p>
    </div>
</body>
</html>
`;

const ERROR_HTML = `
<!DOCTYPE html>
<html>
<head>
    <title>Authentication Failed - Synkro CLI</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            margin: 0;
            background: linear-gradient(135deg, #f87171 0%, #dc2626 100%);
        }
        .container {
            background: white;
            padding: 3rem;
            border-radius: 1rem;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            text-align: center;
            max-width: 400px;
        }
        h1 {
            color: #1f2937;
            margin: 0 0 0.5rem;
        }
        p {
            color: #6b7280;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>Authentication Failed</h1>
        <p>Please try again from your terminal.</p>
    </div>
</body>
</html>
`;

/**
 * Open URL in default browser
 */
function openBrowser(url: string): void {
  const os = platform();
  let bin: string;
  let args: string[];

  switch (os) {
    case "darwin":
      bin = "open";
      args = [url];
      break;
    case "win32":
      // `start` is a cmd built-in, so we host it via cmd /c, but pass the URL
      // as a literal arg through execFile (no shell parsing) to keep shell
      // metacharacters from being interpreted.
      bin = "cmd";
      args = ["/c", "start", "", url];
      break;
    default:
      bin = "xdg-open";
      args = [url];
  }

  // execFile (vs exec) does NOT spawn a shell, so url is treated as a single
  // argv entry regardless of contents. Removes shell-injection risk if url
  // ever ends up containing $/`/;/&/etc.
  execFile(bin, args, (error) => {
    if (error) {
      console.error("Failed to open browser automatically.");
      console.log(`Please open this URL manually: ${url}`);
    }
  });
}

/**
 * Save authentication credentials to file
 */
export function saveCredentials(data: AuthCredentials): void {
  const dir = dirname(AUTH_FILE);

  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true, mode: 0o700 });
  }

  writeFileSync(AUTH_FILE, JSON.stringify(data, null, 2), { mode: 0o600 });
}

/**
 * Load saved authentication credentials
 */
export function loadCredentials(): AuthCredentials | null {
  if (!existsSync(AUTH_FILE)) {
    return null;
  }

  try {
    const content = readFileSync(AUTH_FILE, "utf8");
    return JSON.parse(content);
  } catch (error) {
    return null;
  }
}

/**
 * Create HTTP server to receive OAuth callback.
 *
 * Mirrors packages/desktop/src-tauri/src/auth.rs:
 *   - Listens on PORT
 *   - Handles OPTIONS preflight (CORS)
 *   - Catches /auth?token=...&refresh_token=...&user_id=...&email=...&org_id=...&state=...
 *   - Returns success HTML on token receipt
 */
function createCallbackServer(): Promise<AuthCredentials> {
  // Tokens land via POST body (JSON), never query params, so they don't get
  // logged into req.url, browser DevTools URL bars, server access logs, or
  // tooling that snapshots URLs. CORS origin is pinned to the configured
  // dashboard so a random page on a different origin can't post forged
  // credentials at the local listener.
  const CORS_HEADERS = {
    "Access-Control-Allow-Origin": SYNKRO_WEB_AUTH_URL,
    "Access-Control-Allow-Methods": "POST, OPTIONS",
    "Access-Control-Allow-Headers": "Content-Type",
    "Vary": "Origin",
  };

  return new Promise((resolve, reject) => {
    const server = createServer((req: IncomingMessage, res: ServerResponse) => {
      // CORS preflight — only echo the pinned origin if the request actually
      // comes from there; otherwise omit ACAO so the browser blocks the call.
      if (req.method === "OPTIONS") {
        const origin = req.headers.origin;
        if (origin === SYNKRO_WEB_AUTH_URL) {
          res.writeHead(204, CORS_HEADERS);
        } else {
          res.writeHead(204, {
            "Access-Control-Allow-Methods": "POST, OPTIONS",
            "Access-Control-Allow-Headers": "Content-Type",
            "Vary": "Origin",
          });
        }
        res.end();
        return;
      }

      // Reject requests whose Origin header isn't the dashboard. Browsers
      // send Origin on cross-origin POSTs, so this stops a hostile page from
      // forging credentials at our local listener even if it bypasses CORS.
      const reqOrigin = req.headers.origin;
      if (reqOrigin && reqOrigin !== SYNKRO_WEB_AUTH_URL) {
        res.writeHead(403, { "Vary": "Origin" });
        res.end();
        return;
      }

      if (!req.url) {
        res.writeHead(404, CORS_HEADERS);
        res.end();
        return;
      }

      const url = new URL(req.url, `http://localhost:${PORT}`);

      if (url.pathname !== "/auth") {
        res.writeHead(404, CORS_HEADERS);
        res.end();
        return;
      }

      if (req.method !== "POST") {
        // Reject GET so a stale/older dashboard build can't deliver tokens
        // via query-string. Forces upgrade. CLI 1.0.4+ requires the v1.6+
        // dashboard build.
        res.writeHead(405, { ...CORS_HEADERS, "Allow": "POST, OPTIONS", "Content-Type": "text/html" });
        res.end(ERROR_HTML);
        return;
      }

      // Cap body at 16 KB — JWT pairs are ~4–8 KB; anything bigger is junk.
      const MAX_BODY = 16 * 1024;
      const chunks: Buffer[] = [];
      let total = 0;
      let aborted = false;
      req.on("data", (chunk: Buffer) => {
        if (aborted) return;
        total += chunk.length;
        if (total > MAX_BODY) {
          aborted = true;
          res.writeHead(413, CORS_HEADERS);
          res.end();
          return;
        }
        chunks.push(chunk);
      });
      req.on("end", () => {
        if (aborted) return;
        let parsed: any;
        try {
          parsed = JSON.parse(Buffer.concat(chunks).toString("utf8"));
        } catch {
          res.writeHead(400, { ...CORS_HEADERS, "Content-Type": "application/json" });
          res.end(JSON.stringify({ error: "invalid_json" }));
          setTimeout(() => {
            server.close();
            reject(new Error("Authentication failed: invalid JSON body"));
          }, 200);
          return;
        }

        const token: string | undefined = parsed?.token;
        if (!token || typeof token !== "string") {
          res.writeHead(400, { ...CORS_HEADERS, "Content-Type": "application/json" });
          res.end(JSON.stringify({ error: "missing_token" }));
          setTimeout(() => {
            server.close();
            reject(new Error("Authentication failed: missing token"));
          }, 200);
          return;
        }

        const authData: AuthCredentials = {
          access_token: token,
          refresh_token: typeof parsed.refresh_token === "string" ? parsed.refresh_token : "",
          user_id: typeof parsed.user_id === "string" ? parsed.user_id : undefined,
          email: typeof parsed.email === "string" ? parsed.email : undefined,
          org_id: typeof parsed.org_id === "string" ? parsed.org_id : undefined,
          state: typeof parsed.state === "string" ? parsed.state : undefined,
        };

        res.writeHead(200, { ...CORS_HEADERS, "Content-Type": "application/json" });
        res.end(JSON.stringify({ ok: true }));

        setTimeout(() => {
          server.close();
          resolve(authData);
        }, 200);
      });
      req.on("error", (e) => {
        if (aborted) return;
        aborted = true;
        try { res.writeHead(500, CORS_HEADERS); res.end(); } catch {}
        setTimeout(() => {
          server.close();
          reject(e);
        }, 200);
      });
    });

    server.listen(PORT);

    server.on("error", (error: NodeJS.ErrnoException) => {
      if (error.code === "EADDRINUSE") {
        reject(
          new Error(
            `Port ${PORT} is already in use. Close any other Synkro CLI instance and retry.`,
          ),
        );
      } else {
        reject(error);
      }
    });
  });
}

export type AuthStatus =
  | { phase: 'starting' }
  | { phase: 'browser-opened'; url: string }
  | { phase: 'waiting' }
  | { phase: 'success' }
  | { phase: 'error'; message: string };

/**
 * Initiate the OAuth-style authentication flow
 */
export async function authenticate(
  onStatus?: (status: AuthStatus) => void,
): Promise<AuthCredentials | null> {
  const emit = onStatus || (() => {});

  try {
    emit({ phase: 'starting' });

    // Start local server to receive the callback
    const serverPromise = createCallbackServer();

    // Open browser to the CLI auth page
    const authUrl = `${SYNKRO_WEB_AUTH_URL}/cli-auth?port=${PORT}`;
    openBrowser(authUrl);

    emit({ phase: 'browser-opened', url: authUrl });
    emit({ phase: 'waiting' });

    // Wait for authentication callback
    const data = await serverPromise;

    emit({ phase: 'success' });
    saveCredentials(data);
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    emit({ phase: 'error', message });
    return null;
  }
}

/**
 * Check if user is authenticated (credentials exist and token not expired)
 */
export function isAuthenticated(): boolean {
  const creds = loadCredentials();
  if (!creds) return false;

  // Also check token expiry
  try {
    const decoded = jwt.decode(creds.access_token) as { exp?: number } | null;
    if (!decoded?.exp) return true; // Can't decode, assume valid (refresh will handle it)

    // Consider expired if past expiration (no buffer here — ensureValidToken handles refresh buffer)
    return Date.now() < decoded.exp * 1000;
  } catch {
    return true; // Decode failed, let ensureValidToken handle it
  }
}

/**
 * Get current user ID from JWT token
 */
export function getCurrentUserId(): string {
  const creds = loadCredentials();
  if (!creds) {
    throw new Error("Not authenticated");
  }

  const decoded = jwt.decode(creds.access_token) as WorkOSJwtPayload | null;
  if (!decoded?.sub) {
    throw new Error("Invalid token");
  }

  return decoded.sub;
}

/**
 * Get user info from JWT
 */
export function getUserInfo(): UserInfo {
  const creds = loadCredentials();
  if (!creds) {
    throw new Error("Not authenticated");
  }

  // Prefer the explicit user_id/email/org_id stashed during the OAuth callback
  // (the dashboard returns them as query params). Fall back to JWT decode if
  // we somehow received older creds without those fields.
  if (creds.user_id) {
    return {
      id: creds.user_id,
      email: creds.email ?? '',
      org_id: creds.org_id,
    };
  }

  const decoded = jwt.decode(creds.access_token) as WorkOSJwtPayload | null;
  if (!decoded) {
    throw new Error("Invalid token");
  }

  return {
    id: decoded.sub,
    email: decoded.email ?? '',
    org_id: decoded.org_id,
  };
}

/**
 * Get access token for API calls
 */
export function getAccessToken(): string | null {
  const creds = loadCredentials();
  return creds?.access_token || null;
}

/**
 * Check if token is expired (with 5 min buffer)
 */
export function isTokenExpired(): boolean {
  const creds = loadCredentials();
  if (!creds) return true;

  try {
    const decoded = jwt.decode(creds.access_token) as { exp?: number } | null;
    if (!decoded?.exp) return true;

    // Expired if less than 5 minutes remaining
    const expiresAt = decoded.exp * 1000;
    const buffer = 5 * 60 * 1000; // 5 minutes
    return Date.now() > expiresAt - buffer;
  } catch {
    return true;
  }
}

/**
 * Refresh the access token using refresh_token
 */
export async function refreshToken(): Promise<boolean> {
  const creds = loadCredentials();
  if (!creds?.refresh_token) return false;

  try {
    const response = await fetch(`${SYNKRO_API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: creds.refresh_token }),
    });

    if (!response.ok) return false;

    const data = await response.json();
    if (data.access_token) {
      saveCredentials({
        ...creds,
        access_token: data.access_token,
        refresh_token: data.refresh_token || creds.refresh_token,
      });
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

// Mutex: prevent concurrent token refresh races
let refreshPromise: Promise<boolean> | null = null;

/**
 * Ensure we have a valid token, refreshing if needed
 */
export async function ensureValidToken(): Promise<boolean> {
  if (!isAuthenticated()) return false;

  if (isTokenExpired()) {
    if (!refreshPromise) {
      refreshPromise = refreshToken().finally(() => { refreshPromise = null; });
    }
    const refreshed = await refreshPromise;
    if (!refreshed) {
      clearCredentials();
      return false;
    }
  }

  return true;
}

/**
 * Clear saved credentials (logout)
 */
export function clearCredentials(): void {
  if (existsSync(AUTH_FILE)) {
    unlinkSync(AUTH_FILE);
  }
}

/**
 * Get secrets for a user's integrations
 * In production, this would fetch from Infisical vault using the access token
 *
 * These are secrets the USER provides for THEIR integrations:
 * - AWS credentials (for their S3 buckets)
 * - HuggingFace token (for their datasets)
 * - Langsmith API key (for their projects)
 */
export async function getSecrets(
  userId: string,
  integrationId: string,
): Promise<Record<string, string>> {
  // TODO: In production, use access token to fetch from Infisical
  // For now, return from environment (dev mode)
  return {
    AWS_ACCESS_KEY_ID: process.env.USER_AWS_KEY || "",
    AWS_SECRET_ACCESS_KEY: process.env.USER_AWS_SECRET || "",
    AWS_REGION: process.env.USER_AWS_REGION || "us-east-1",
    HF_TOKEN: process.env.USER_HF_TOKEN || "",
    LANGSMITH_API_KEY: process.env.USER_LANGSMITH_KEY || "",
  };
}
